import { classNames, cn } from '@commenda/utils';
import { flexRender, Table as TanStackTable } from '@tanstack/react-table';
import { Checkbox } from '../../Checkbox';
import { Icons } from '../../Icons';
import { TableCell as TCell, TableRow as TRow } from '../../Table';
import {
  ColumnGroup,
  CustomColumnDef,
  RowHeightView,
  TreeNode,
} from '../types';
import {
  getColumnId,
  getColumnWidthStyle,
  getStickyColumnClasses,
  getStickyPositionLive,
} from '../utils';
import { HeaderCell } from './HeaderCell';

interface TableGroupRowProps<TData, TValue> {
  groupKey: string;
  rows: TData[];
  isExpanded: boolean;
  onToggle: () => void;
  groups: ColumnGroup<TreeNode<TData>, TValue>[];
  table: TanStackTable<TreeNode<TData>>;
  renderGroupHeader: (key: string, rows: TData[]) => React.ReactNode;
  /** Max height for scrolling within group - currently not used as it breaks column alignment */
  maxGroupHeight: number;
  borderClassName?: string;
  isFirstGroup?: boolean;
  // Pass through existing props
  onRowClick?: (row: TData) => void;
  rowClassName: (row: TData) => string;
  rowHeightView: RowHeightView;
  tableCellClassName: string;
  tableRowClassName: string;
  tableHeadClassName: string;
  /** Width of the visible scroll container for sticky group headers */
  containerWidth?: number;
  rowSelectionControl?: {
    isEnabled: boolean;
    isRowSelected: (row: TData) => boolean;
    toggleRowById: (id: string) => void;
    toggleAll: (subset?: TData[]) => void;
    getRowId: (row: TData) => string;
  };
}

export function TableGroupRow<TData, TValue>({
  groupKey,
  rows,
  isExpanded,
  onToggle,
  groups,
  table,
  renderGroupHeader,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  maxGroupHeight: _maxGroupHeight, // Not used - nested table approach breaks column alignment
  borderClassName,
  onRowClick,
  rowClassName,
  rowHeightView,
  tableCellClassName,
  tableRowClassName,
  tableHeadClassName,
  containerWidth,
  rowSelectionControl,
  isFirstGroup,
}: TableGroupRowProps<TData, TValue>) {
  const flattenedColumns = groups.flatMap((group) => group.columns);
  // Respect current column visibility from TanStack state
  const totalColumns = flattenedColumns.reduce((count, col) => {
    const id = getColumnId(col);
    const isVisible = table.getColumn(id)?.getIsVisible?.();
    return count + (isVisible ? 1 : 0);
  }, 0);

  const rowHeight =
    rowHeightView === 'expanded' ? '!min-h-[52px]' : '!min-h-[44px]';

  return (
    <>
      {/* 4px spacer between groups (skip before the first group).
          Elevated above the scroll-edge shadow strips (z-40) so the gradient
          doesn't bleed through the spacer. */}
      {!isFirstGroup && (
        <TRow
          data-element="group-spacer"
          style={{ borderTopWidth: 0, borderBottomWidth: 0 }}
        >
          <TCell colSpan={totalColumns} className="h-xs p-0 border-0">
            <div className="relative z-50 h-xs w-full bg-bg-primary" />
          </TCell>
        </TRow>
      )}
      {/* Group Header Row */}
      <TRow
        onClick={onToggle}
        className={classNames('group cursor-pointer', rowHeight)}
        style={{ borderTopWidth: 0, borderBottomWidth: 0 }}
        data-element="group-header"
        data-group-key={groupKey}
      >
        <TCell colSpan={totalColumns} className="p-0">
          <div
            className={classNames(
              'sticky left-0 z-50 px-4 rounded-md bg-bg-secondary group-hover:bg-bg-secondary-hover flex items-center',
              rowHeightView === 'expanded' ? 'py-2.5' : 'py-0',
              rowHeight
            )}
            style={
              containerWidth ? { width: `${containerWidth}px` } : undefined
            }
          >
            <div className="flex items-center w-full">
              <div className="flex items-center gap-3">
                <Icons.ChevronRight
                  className={classNames(
                    'size-4 transition-transform duration-200',
                    isExpanded ? 'rotate-90' : ''
                  )}
                />
                {renderGroupHeader(groupKey, rows)}
              </div>
            </div>
          </div>
        </TCell>
      </TRow>

      {/* Expanded Content - rendered directly in outer table for sticky to work */}
      {isExpanded && (
        <>
          {/* Column Headers Row - direct in outer table */}
          <TRow
            className="border-b border-border-primary bg-bg-primary"
            style={{ borderTopWidth: 0 }}
          >
            {groups.map((group) =>
              group.columns.map((column, groupColIdx) => {
                const colId = getColumnId(column);
                const columnApi = table.getColumn(colId);
                if (!columnApi?.getIsVisible?.()) return null;

                const effectiveWidth = column.width;
                const allVisibleColumns = groups
                  .flatMap((g) => g.columns)
                  .filter((c) =>
                    table.getColumn(getColumnId(c))?.getIsVisible?.()
                  );
                const isLastVisibleHeader =
                  allVisibleColumns.length > 0 &&
                  getColumnId(
                    allVisibleColumns[allVisibleColumns.length - 1]
                  ) === colId;
                const canSort = !!table.getColumn(colId)?.getCanSort?.();
                const isPinned = !!group.sticky;
                const visibleGroupHeaders = groups
                  .find((g) => g.id === group.id)
                  ?.columns.filter((c) =>
                    table.getColumn(getColumnId(c))?.getIsVisible?.()
                  );
                const isFirstInGroup =
                  visibleGroupHeaders?.[0] &&
                  getColumnId(visibleGroupHeaders[0]) === colId;
                const isLastInGroup =
                  visibleGroupHeaders?.[visibleGroupHeaders.length - 1] &&
                  getColumnId(
                    visibleGroupHeaders[visibleGroupHeaders.length - 1]
                  ) === colId;

                return (
                  <HeaderCell
                    key={`${group.id}-${colId}`}
                    className={classNames(
                      'relative',
                      '',
                      getStickyColumnClasses(group, { isHeader: true }),
                      tableHeadClassName
                    )}
                    style={{
                      ...getColumnWidthStyle(effectiveWidth),
                      ...(group.sticky === 'left' && {
                        left: `${getStickyPositionLive(table, groups, group, groupColIdx)}px`,
                      }),
                      ...(group.sticky === 'right' && {
                        right: `${getStickyPositionLive(table, groups, group, groupColIdx)}px`,
                      }),
                    }}
                    sort={
                      canSort
                        ? {
                            direction: table
                              .getColumn(colId)
                              ?.getIsSorted?.() as 'asc' | 'desc' | null,
                            onToggle: () =>
                              table.getColumn(colId)?.toggleSorting?.(),
                          }
                        : undefined
                    }
                    columnOptions={
                      canSort && !isPinned
                        ? {
                            columnDef: column as unknown as CustomColumnDef<
                              unknown,
                              unknown
                            >,
                            onSortAsc: () =>
                              table.getColumn(colId)?.toggleSorting?.(false),
                            onSortDesc: () =>
                              table.getColumn(colId)?.toggleSorting?.(true),
                            onHideColumn: () =>
                              table.getColumn(colId)?.toggleVisibility?.(false),
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {(() => {
                        const firstVisible = groups
                          .flatMap((g) => g.columns)
                          .find((c) =>
                            table.getColumn(getColumnId(c))?.getIsVisible?.()
                          );
                        const isFirst =
                          !!firstVisible && getColumnId(firstVisible) === colId;
                        if (isFirst && rowSelectionControl?.isEnabled) {
                          const total = rows.length;
                          const selectedCount = rows.filter((r) =>
                            rowSelectionControl.isRowSelected(r)
                          ).length;
                          const all = total > 0 && selectedCount === total;
                          const some =
                            selectedCount > 0 && selectedCount < total;
                          return (
                            <Checkbox
                              className="checkbox-cell"
                              aria-label="Select group"
                              checked={all}
                              indeterminate={some}
                              onClick={(e) => e.stopPropagation()}
                              onCheckedChange={() =>
                                rowSelectionControl.toggleAll?.(
                                  rows as unknown as TData[]
                                )
                              }
                            />
                          );
                        }
                        return null;
                      })()}
                      <div className="min-w-0 flex-1">
                        {typeof column.header === 'function'
                          ? flexRender(
                              column.header,
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              { getValue: () => undefined } as any
                            )
                          : column.header}
                      </div>
                    </div>
                  </HeaderCell>
                );
              })
            )}
          </TRow>

          {/* Data Rows - direct in outer table */}
          {rows.map((row, index) => (
            <TRow
              key={`${groupKey}-${(row as Record<string, unknown>)?.id ?? index}`}
              onClick={(e) => {
                if (
                  onRowClick &&
                  !(e.target as HTMLElement).closest('.checkbox-cell')
                ) {
                  onRowClick(row);
                }
              }}
              className={classNames(
                // Hover bg unconditional — keeps non-sticky cells in lock-step
                // with sticky cells' built-in group-hover. See TableRow.tsx.
                'border-b border-border-primary last:border-b-0 group hover:bg-bg-primary-hover',
                onRowClick ? 'cursor-pointer' : 'cursor-default',
                rowClassName(row),
                tableRowClassName
              )}
            >
              {groups.map((group) =>
                group.columns.map((column, columnIndex) => {
                  const columnId = getColumnId(column);
                  const columnApi = table.getColumn(columnId);
                  if (!columnApi?.getIsVisible?.()) return null;
                  const cellValue = (row as Record<string, unknown>)[columnId];

                  const effectiveWidth = column.width;
                  const allVisibleColumns = groups
                    .flatMap((g) => g.columns)
                    .filter((c) =>
                      table.getColumn(getColumnId(c))?.getIsVisible?.()
                    );
                  const isLastVisibleCell =
                    allVisibleColumns.length > 0 &&
                    getColumnId(
                      allVisibleColumns[allVisibleColumns.length - 1]
                    ) === columnId;

                  const visibleGroupColumns = group.columns.filter((c) =>
                    table.getColumn(getColumnId(c))?.getIsVisible?.()
                  );
                  const isFirstInGroup = visibleGroupColumns[0] === column;
                  const isLastInGroup =
                    visibleGroupColumns[visibleGroupColumns.length - 1] ===
                    column;

                  const dataRowHeight =
                    rowHeightView === 'expanded' ? '!h-[52px]' : '!h-11';
                  return (
                    <TCell
                      key={`${group.id}-${columnId}`}
                      className={classNames(
                        'px-4 relative',
                        '',
                        getStickyColumnClasses(group, { isHeader: false }),
                        rowHeightView === 'expanded' ? 'py-2.5' : 'py-0',
                        dataRowHeight,
                        tableCellClassName
                      )}
                      style={{
                        ...getColumnWidthStyle(effectiveWidth),
                        ...(group.sticky === 'left' && {
                          left: `${getStickyPositionLive(table, groups, group, columnIndex)}px`,
                        }),
                        ...(group.sticky === 'right' && {
                          right: `${getStickyPositionLive(table, groups, group, columnIndex)}px`,
                        }),
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {(() => {
                          const firstVisible = groups
                            .flatMap((g) => g.columns)
                            .find((c) =>
                              table.getColumn(getColumnId(c))?.getIsVisible?.()
                            );
                          const isFirst =
                            !!firstVisible &&
                            getColumnId(firstVisible) === columnId;
                          if (isFirst && rowSelectionControl?.isEnabled) {
                            return (
                              <Checkbox
                                className="checkbox-cell shrink-0"
                                aria-label="Select row"
                                checked={rowSelectionControl.isRowSelected(
                                  row as TData
                                )}
                                onCheckedChange={() =>
                                  rowSelectionControl.toggleRowById(
                                    rowSelectionControl.getRowId(row as TData)
                                  )
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            );
                          }
                          return null;
                        })()}
                        <div className="min-w-0 flex-1">
                          {column.cell
                            ? flexRender(
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                column.cell as any,
                                {
                                  getValue: () => cellValue,
                                  row: { original: row },
                                  column: { id: columnId },
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                } as any
                              )
                            : (cellValue as React.ReactNode)}
                        </div>
                      </div>
                    </TCell>
                  );
                })
              )}
            </TRow>
          ))}

          {/* Bold Separator Row */}
          <TRow data-element="group-separator">
            <TCell
              colSpan={totalColumns}
              className={cn(
                borderClassName
                  ? 'h-0 p-0 border-0'
                  : 'h-0 p-0 border-b-3 border-border-primary',
                borderClassName
              )}
            />
          </TRow>
        </>
      )}
    </>
  );
}
